import { autorun, makeAutoObservable, onBecomeUnobserved, runInAction } from 'mobx';
import { FETCH_STATE } from '../../consts/storage.consts';
import { getNodeStorage } from 'services/storage.services';

export class TreeStore {
  items = [];
  childrenMap = {};
  expandedNodes = [];
  selectedNode = null;
  localStorageId = '';
  fetchState = FETCH_STATE.NONE;
  isDelete = false;
  queryFilter = null;
  getItems = undefined;

  constructor(localStorageId = '') {
    makeAutoObservable(this);

    runInAction(() => {
      this.localStorageId = localStorageId;
    });

    const storeJSON = localStorage.getItem(localStorageId);
    if (storeJSON) Object.assign(this, JSON.parse(storeJSON));

    autorun(() => {
      if (!this.isDelete) {
        localStorage.setItem(localStorageId, JSON.stringify(this));
      }
    });

    window.addEventListener('storage', this._updateStore);
    onBecomeUnobserved(this, 'items', this._dispose);
  }

  _updateStore = (e) => {
    if (e.key === this.localStorageId) {
      const storeJSON = localStorage.getItem(this.localStorageId);

      if (storeJSON) Object.assign(this, JSON.parse(storeJSON));
    }
  };

  _dispose = () => {
    window.removeEventListener('storage', this._updateStore);
  };

  loadItems = async (getItems) => {
    runInAction(() => {
      this.getItems = getItems;
      this.isDelete = false;
      this.fetchState = FETCH_STATE.PENDING;
      this.childrenMap = {};
      this.expandedNodes = [];
      this.selectedNode = null;
    });
    try {
      const res = await getItems(this.queryFilter);
      runInAction(() => {
        this.items = res || [];
        this.fetchState = FETCH_STATE.SUCCESS;
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.fetchState = FETCH_STATE.ERROR;
      });
    }
  };

  addChildren = (nodeId, children) => {
    this.childrenMap = {
      ...this.childrenMap,
      [nodeId]: children || [],
    };
  };

  toggleNode = (nodeId) => {
    const index = this.expandedNodes.findIndex((id) => id === nodeId);

    if (index !== -1) {
      this.expandedNodes = [
        ...this.expandedNodes.slice(0, index),
        ...this.expandedNodes.slice(index + 1),
      ];
    } else {
      this.expandedNodes = [...this.expandedNodes, nodeId];
    }
  };

  setSelectedNode = (node) => {
    this.selectedNode = node;
  };

  moveItem = (itemId, destinationIndex, sourceParentId, destinationParentId) => {
    itemId = String(itemId);
    sourceParentId = sourceParentId ? String(sourceParentId) : null;
    destinationParentId = destinationParentId ? String(destinationParentId) : null;

    const sourceArray = sourceParentId ? this.childrenMap[sourceParentId] || [] : this.items;

    const sourceIndex = sourceArray.findIndex((item) => String(this.getItemId(item)) === itemId);
    if (sourceIndex === -1) return;

    const [movedItem] = sourceArray.splice(sourceIndex, 1);

    if (sourceParentId === destinationParentId) {
      sourceArray.splice(destinationIndex, 0, movedItem);

      if (sourceParentId) {
        this.childrenMap = {
          ...this.childrenMap,
          [sourceParentId]: [...sourceArray],
        };
      } else {
        this.items = [...sourceArray];
      }
    } else {
      const destArray = destinationParentId
        ? this.childrenMap[destinationParentId] || []
        : this.items;

      destArray.splice(destinationIndex, 0, movedItem);

      if (sourceParentId) {
        this.childrenMap = {
          ...this.childrenMap,
          [sourceParentId]: [...sourceArray],
        };
      } else {
        this.items = [...sourceArray];
      }

      if (destinationParentId) {
        this.childrenMap = {
          ...this.childrenMap,
          [destinationParentId]: [...destArray],
        };
      } else {
        this.items = [...destArray];
      }
    }
  };

  getItemId = (item, field = 'id') => {
    return item[field];
  };

  setQueryString = (value) => {
    this.queryFilter = value;
  };

  refresh = () => {
    runInAction(() => {
      this.childrenMap = {};
      this.expandedNodes = [];
      this.items = [];
      this.loadItems(this.getItems);
    });
  };

  reset = () => {
    runInAction(() => {
      this.childrenMap = {};
      this.expandedNodes = [];
      this.selectedNode = null;
      this.items = [];
      this.isDelete = true;
      this.queryFilter = null;
      this.getItems = undefined;
      if (this.localStorageId) {
        localStorage.removeItem(this.localStorageId);
      }
    });
  };

  fetchNode = async (nodeId, type = 'storage') => {
    try {
      const res = await getNodeStorage(nodeId, type);

      const updatedNode = res;

      runInAction(() => {
        this.items = this.items.map((item) =>
          this.getItemId(item) === nodeId ? updatedNode : item,
        );

        Object.keys(this.childrenMap).forEach((parentId) => {
          this.childrenMap[parentId] = this.childrenMap[parentId].map((child) =>
            this.getItemId(child) === nodeId ? updatedNode : child,
          );
        });

        if (this.selectedNode && this.getItemId(this.selectedNode) === nodeId) {
          this.selectedNode = updatedNode;
        }
      });

      return updatedNode;
    } catch (error) {
      console.error('Ошибка при загрузке ноды:', error);
      throw error;
    }
  };
}

export const treeStore = new TreeStore('storage_tree');
export const treeStorageSelectStore = new TreeStore('storage_select_tree');
